import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JsonLd from '../seo/JsonLd';

export interface Crumb {
  label: string;
  /** Route for intermediate crumbs; omit on the current (last) page. */
  to?: string;
}

const SITE_ORIGIN = 'https://safaqat24.sa';

/**
 * Visible breadcrumb trail + matching BreadcrumbList structured data.
 * RTL-aware (the chevron flips with direction automatically since it's a
 * plain glyph between flex items in dir-aware order).
 */
export default function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  if (crumbs.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.label,
      ...(crumb.to ? { item: `${SITE_ORIGIN}${crumb.to}` } : {}),
    })),
  };

  return (
    <nav aria-label={isRTL ? 'مسار التنقل' : 'Breadcrumb'} dir={i18n.dir()}>
      <JsonLd data={schema} />
      <ol className="flex flex-wrap items-center gap-1 text-[12.5px] text-[var(--pv-color-text-muted)]">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <li key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1">
              {index > 0 && (
                <i className={`${isRTL ? 'ri-arrow-left-s-line' : 'ri-arrow-right-s-line'} text-[var(--pv-color-text-subtle)]`} aria-hidden="true"></i>
              )}
              {crumb.to && !isLast ? (
                <Link
                  to={crumb.to}
                  className="max-w-[14rem] truncate rounded font-medium transition-colors hover:text-[#004D40] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="max-w-[16rem] truncate font-semibold text-[var(--pv-color-text)]" aria-current={isLast ? 'page' : undefined}>
                  {crumb.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
